import React, {FC, useState} from 'react'
import {Dropdown} from 'react-bootstrap'
import Tooltip from 'react-bootstrap/Tooltip'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import {useDispatch, useSelector} from 'react-redux'
import {appointmentsCallenderSortBy, appointmentsCallenderSortOrder} from '../../../redux/tableSlice/tableSlice'
import './style.css'

type SortOption = 'time' | 'client' | 'language' | 'communicationType' | 'linguist'

const sortOptions: {value: SortOption; label: string}[] = [
  {value: 'time', label: 'Time'},
  {value: 'client', label: 'Account'},
  {value: 'language', label: 'Language'},
  {value: 'communicationType', label: 'Communication Type'},
]

type Props = {
  onSortChange?: () => void
  showLinguistOption?: boolean
}

export function CalendarSortDropdown({onSortChange, showLinguistOption = true}: Props) {
  const [showDropdownMenu, setShowDropdownMenu] = useState(false)
  const dispatch = useDispatch()

  const {sortBy, sortOrder} = useSelector((state: any) => ({
    sortBy: state.table.appointmentsCallender[0].sortBy,
    sortOrder: state.table.appointmentsCallender[0].sortOrder,
  }))

  const [tempSortBy, setTempSortBy] = useState<SortOption>(sortBy)
  const [tempSortOrder, setTempSortOrder] = useState<'asc' | 'desc'>(sortOrder)

  const isSortApplied = sortBy !== 'time' || sortOrder !== 'asc'

  const allSortOptions = showLinguistOption
    ? [...sortOptions, {value: 'linguist' as SortOption, label: 'Linguist'}]
    : sortOptions

  const getSelectedLabel = () => {
    const option = allSortOptions.find((opt) => opt.value === sortBy)
    return option?.label || 'Time'
  }

  const handleApplySort = () => {
    dispatch(appointmentsCallenderSortBy(tempSortBy))
    dispatch(appointmentsCallenderSortOrder(tempSortOrder))
    setShowDropdownMenu(false)
    if (onSortChange) {
      onSortChange()
    }
  }

  const handleClearSort = () => {
    setTempSortBy('time')
    setTempSortOrder('asc')
    dispatch(appointmentsCallenderSortBy('time'))
    dispatch(appointmentsCallenderSortOrder('asc'))
    setShowDropdownMenu(false)
    if (onSortChange) {
      onSortChange()
    }
  }

  const handleDropdownToggle = (nextShow: boolean) => {
    if (nextShow) {
      setTempSortBy(sortBy)
      setTempSortOrder(sortOrder)
    }
    setShowDropdownMenu(nextShow)
  }

  return (
    <div className='d-flex align-items-center'>
      <Dropdown onToggle={handleDropdownToggle} show={showDropdownMenu}>
        <Dropdown.Toggle
          variant='light'
          id='dropdown-sort'
          className={`btn btn-sm d-flex align-items-center no-caret ${isSortApplied ? 'btn-light-primary' : ''}`}
          style={{minWidth: '120px'}}
        >
          <i className='bi bi-sort-down-alt me-2'></i>
          <span>{getSelectedLabel()}</span>
          <i className='bi bi-chevron-down ms-2'></i>
        </Dropdown.Toggle>
        {showDropdownMenu && (
          <Dropdown.Menu className='p-4' style={{minWidth: '240px'}}>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <div className='d-flex align-items-center'>
                <select
                  className='form-select form-select-sm'
                  value={tempSortBy}
                  onChange={(e) => setTempSortBy(e.target.value as SortOption)}
                  style={{minWidth: '150px'}}
                >
                  {allSortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light ms-2'
                onClick={() => setTempSortOrder(tempSortOrder === 'asc' ? 'desc' : 'asc')}
              >
                <i className={`bi bi-sort-${tempSortOrder === 'asc' ? 'down' : 'up'} fs-5`}></i>
              </button>
            </div>
            <div className='separator border-gray-200 mb-3'></div>
            <div className='mb-3'>
              {allSortOptions.map((option) => (
                <div
                  key={option.value}
                  className={`d-flex align-items-center p-2 rounded cursor-pointer ${
                    tempSortBy === option.value ? 'bg-light-primary' : 'bg-hover-light'
                  }`}
                  onClick={() => setTempSortBy(option.value)}
                  style={{cursor: 'pointer'}}
                >
                  <span className='me-3' style={{width: '20px'}}>
                    {tempSortBy === option.value && (
                      <i className='bi bi-check2 text-primary fs-4'></i>
                    )}
                  </span>
                  <span className='fs-7 text-gray-700'>{option.label}</span>
                </div>
              ))}
            </div>
            <div className='separator border-gray-200 mb-3'></div>
            <div className='d-flex justify-content-between'>
              <button
                type='button'
                className='btn btn-sm btn-light'
                onClick={handleClearSort}
              >
                Clear Sort
              </button>
              <button
                type='button'
                className='btn btn-sm btn-primary'
                onClick={handleApplySort}
              >
                Apply Sort
              </button>
            </div>
          </Dropdown.Menu>
        )}
      </Dropdown>
    </div>
  )
}
